import React, { useCallback, useState } from "react";
import { Box, Typography, IconButton, Menu, MenuItem, Avatar, useTheme } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { MbscResource } from "@mobiscroll/react";
import { useNavigate } from "react-router-dom";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import { Permission } from "~/hooks/permissions";

interface PatientCalendarItemProps {
  resource: MbscResource;
  onAddVisit?: (patientId: number, firstName: string, lastName: string) => void;
}

const PatientCalendarItem: React.FC<PatientCalendarItemProps> = ({ resource, onAddVisit }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const patientId = typeof resource.id === "number" ? resource.id : parseInt(`${resource.id}`, 10);

  const initials = resource.name
    ? resource.name
        .split(" ")
        .filter(Boolean)
        .map((part) => part[0])
        .join("")
        .slice(0, 2)
    : "";

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleAddVisit = useCallback(() => {
    if (onAddVisit && Number.isFinite(patientId)) {
      const nameParts = (resource.name || "").split(" ");
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";
      onAddVisit(patientId, firstName, lastName);
    }
    handleMenuClose();
  }, [handleMenuClose, onAddVisit, patientId, resource.name]);

  const handleOpenProfile = useCallback(() => {
    if (Number.isFinite(patientId)) {
      navigate(`/patients/${patientId}`);
    }
    handleMenuClose();
  }, [handleMenuClose, navigate, patientId]);
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
        <Avatar
            sx={{ bgcolor: theme.palette.primary.light, fontSize: 14, width: 30, height: 30 }}
            src=""
            alt={`${resource.firstName || ''} ${resource.lastName || ''}`}
          >
            {initials}
        </Avatar>
        <Box sx={{ ml: 1 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: "normal" }} component="div">
            {resource.name}
          </Typography>
        </Box>
      </Box>
      {Number.isFinite(patientId) && (
        <>
          <IconButton size="small" onClick={handleMenuOpen}>
            <ArrowDropDownIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <PermissionOrHide permission={Permission.createOverrideVisits}>
              <MenuItem onClick={handleAddVisit}>Add Visit</MenuItem>
            </PermissionOrHide>
            <MenuItem onClick={handleOpenProfile}>Open Profile</MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
};

export default PatientCalendarItem;
